import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { logger } from '../services/logger';

export interface DeviceHistoryEntry {
  id: string;
  timestamp: string;
  action: string;
  status: 'success' | 'error';
  user: string;
  personName?: string;
  personId?: string;
  personPhoto?: string;
  direction?: string;
  eventType?: string;
  departmentName?: string;
  unitName?: string;
  personRole?: string;
}

interface UseDeviceHistoryResult {
  history: DeviceHistoryEntry[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export const useDeviceHistory = (
  deviceId: string | null,
  customerId: string | null = null,
  limit: number = 50
): UseDeviceHistoryResult => {
  const [history, setHistory] = useState<DeviceHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!deviceId || !customerId) {
      setHistory([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // NOT /access-logs — that route is wired to a resident-self-service handler that
      // ignores device_id and 403s for staff/admin callers with no personal Person record
      // (see 2026-08-16 investigation, same issue documented in Units.tsx/DepartmentDetail.tsx).
      // /events/access is the real filter-respecting endpoint.
      const response = await api.get('/events/access', {
        params: {
          customer_id: customerId,
          device_id: deviceId,
          limit
        }
      });

      // API returns { data: events[], total: number }
      console.log('useDeviceHistory response:', response.data);
      const logs = response.data?.data || response.data?.events || response.data?.logs || [];

      // Transform logs to history entries
      const historyEntries: DeviceHistoryEntry[] = logs.map((log: any) => {
        const eventDate = new Date(log.event_timestamp || log.created_at || log.updated_at);
        const timestamp = eventDate.toLocaleString('pt-BR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });

        // Determine action description based on the event_type and status fields
        let actionLabel = 'Evento';
        const eventType = log.event_type || '';
        const direction = log.direction || '';
        const isAuthorized = log.is_authorized !== false && log.status !== 'failure';
        // If a physical direction is present the event happened regardless of PantherAccess authorization
        const hasDirection = direction === 'entrada' || direction === 'saida' || direction === 'passagem';

        if (eventType === 'remote_door_trigger' || eventType === 'remote_user') {
          actionLabel = 'Abertura Remota';
        } else if (direction === 'entrada') {
          // Direction wins over is_authorized — the door physically opened
          actionLabel = 'Entrada Autorizada';
        } else if (direction === 'saida') {
          actionLabel = 'Saída Registrada';
        } else if (direction === 'passagem') {
          actionLabel = 'Passagem';
        } else if (eventType === 'access_granted' || isAuthorized) {
          actionLabel = 'Entrada Autorizada';
        } else if (eventType === 'access_denied' || !isAuthorized) {
          actionLabel = 'Acesso Negado';
        } else if (eventType === 'access_attempt') {
          actionLabel = 'Tentativa de Acesso';
        } else if (eventType.includes('override')) {
          actionLabel = 'Acionamento Manual';
        }

        return {
          id: log.id,
          timestamp,
          action: actionLabel,
          status: (hasDirection || isAuthorized) ? 'success' : 'error',
          user: log.person_name || log.actor_name || 'Desconhecido',
          personName: log.person_name || log.actor_name,
          personId: log.person_id,
          personPhoto: log.identity_photo_url || log.face_photo_url || log.photo_url || log.person_photo_url || log.person_photo || log.profile_picture_url,
          direction: log.direction,
          eventType: log.event_type,
          departmentName: log.department_name,
          unitName: log.unit_name,
          personRole: log.person_role,
        };
      });

      setHistory(historyEntries);
      logger.info('useDeviceHistory', 'Loaded device history', {
        deviceId,
        customerId,
        count: historyEntries.length,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch device history');
      setError(error);
      logger.error('useDeviceHistory', 'Error fetching device history', err);
    } finally {
      setIsLoading(false);
    }
  }, [deviceId, customerId, limit]);

  // Fetch when deviceId changes
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    isLoading,
    error,
    refresh: fetchHistory,
  };
};
